/**
 * 模板编辑历史：撤销 / 重做。
 *
 * 画布现在能右键删行删列、双击改字、拖着改列宽 —— 一步错就回不去，是竞品
 * 对比里最刺眼的一条。这里不侵入任何写入路径：只盯着 template 这个对象的
 * 变化，每变一次就把上一版存进栈。撤销 = 把栈顶那版整个写回去。
 *
 * 为什么不给每个操作单独记「反操作」：写入口太多（面板、画布、AI 生成、
 * 模板库），漏一个就是一处不能撤销的黑洞。盯对象变化是唯一能覆盖全部的办法。
 *
 * 连续快速的改动（打字、拖列宽）合并成一步：600ms 内的变化不再压栈，
 * 否则撤销一次只退一个字符，用户要按几十下。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContractTemplate } from './types';

type Snapshot = Pick<ContractTemplate, 'name' | 'sections' | 'settings' | 'overlays' | 'visibility'>;
type Patch = Partial<Snapshot>;

const COALESCE_MS = 600;
const MAX_DEPTH = 100;

function snap(t: ContractTemplate): Snapshot {
  return { name: t.name, sections: t.sections, settings: t.settings, overlays: t.overlays, visibility: t.visibility };
}
function same(a: Snapshot, b: Snapshot): boolean {
  return (
    a.name === b.name &&
    a.sections === b.sections &&
    a.settings === b.settings &&
    a.overlays === b.overlays &&
    a.visibility === b.visibility
  );
}

export interface History {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useTemplateHistory(
  template: ContractTemplate | null,
  apply: (patch: Patch) => void,
): History {
  const past = useRef<Snapshot[]>([]);
  const future = useRef<Snapshot[]>([]);
  const last = useRef<Snapshot | null>(null);
  const lastAt = useRef(0);
  // 撤销/重做自己触发的变化不能再压栈，否则一撤销栈又长回去
  const silent = useRef(false);
  const tplId = useRef<string | null>(null);
  const [, bump] = useState(0);

  useEffect(() => {
    if (!template) return;
    // 换了一个模板：历史清零，别把 A 模板的旧版撤销进 B
    if (tplId.current !== template.id) {
      tplId.current = template.id;
      past.current = [];
      future.current = [];
      last.current = snap(template);
      lastAt.current = 0;
      bump((n) => n + 1);
      return;
    }
    const cur = snap(template);
    const prev = last.current;
    if (prev && same(prev, cur)) return;
    if (silent.current) {
      silent.current = false;
      last.current = cur;
      return;
    }
    const now = Date.now();
    if (prev && now - lastAt.current > COALESCE_MS) {
      past.current.push(prev);
      if (past.current.length > MAX_DEPTH) past.current.shift();
      future.current = [];
      bump((n) => n + 1);
    }
    lastAt.current = now;
    last.current = cur;
  }, [template]);

  const undo = useCallback(() => {
    const prev = past.current.pop();
    if (!prev || !last.current) return;
    future.current.push(last.current);
    silent.current = true;
    lastAt.current = 0;
    apply(prev);
    bump((n) => n + 1);
  }, [apply]);

  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next || !last.current) return;
    past.current.push(last.current);
    silent.current = true;
    lastAt.current = 0;
    apply(next);
    bump((n) => n + 1);
  }, [apply]);

  // 宿主页面上的快捷键。画布 iframe 里的按键由注入脚本转发成 pp-undo/pp-redo。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      // 正在输入框里打字：交给输入框自己的撤销
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return { undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0 };
}
